import { Injectable } from '@angular/core'
import { EMPTY, Observable, race, timer } from 'rxjs'
import { catchError, first, map, switchMap, tap } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { JobDto, JobStatus } from 'src/app/services/dtos'
import { handleArpioError } from 'src/utils/handle-error'

type Outcome = 'completed' | 'failed' | 'timeout'

// Polls the StackSets template-regeneration job that gates Step 3 of the
// setup flow and the update-stack-set command UI. Centralised here because
// stacksets-instructions and stacksets-update-instructions both run the
// same lifecycle.
@Injectable({ providedIn: 'root' })
export class StacksetsRegenerationPollService {
  private readonly POLL_INTERVAL_MS = 5000
  private readonly DEFAULT_TIMEOUT_MS = 120000
  // Mutable so the /test_page "Simulated timeout" scenario can shorten the wait.
  private timeoutMs = this.DEFAULT_TIMEOUT_MS

  constructor(
    private readonly apiService: ApiService,
    private readonly uiService: UiService
  ) {}

  setTimeoutMsForTesting(ms: number): void {
    this.timeoutMs = ms
  }

  resetTimeoutMsForTesting(): void {
    this.timeoutMs = this.DEFAULT_TIMEOUT_MS
  }

  // Emits exactly once when the regen job reaches a terminal status (done/failed/skipped)
  // or the 120s timeout fires, then completes. Surfaces user-visible feedback for
  // failed and timeout outcomes; callers only need to manage their inProgress flag.
  pollUntilDone(accountId: string, jobId: string): Observable<Outcome> {
    const isTerminal = (s: JobStatus) => s === 'done' || s === 'failed' || s === 'skipped'

    // timer(0, ...) emits immediately so cache-hit (already-done) jobs unblock without waiting a poll interval.
    const polling$: Observable<Outcome> = timer(0, this.POLL_INTERVAL_MS).pipe(
      switchMap(() => this.apiService.getJob(accountId, jobId)),
      first((job: JobDto) => isTerminal(job.status)),
      map((job: JobDto): Outcome => (job.status === 'failed' ? 'failed' : 'completed'))
    )

    const timeout$: Observable<Outcome> = timer(this.timeoutMs).pipe(map(() => 'timeout'))

    return race(polling$, timeout$).pipe(
      tap((outcome) => {
        if (outcome === 'failed') {
          // Outcome of a background job the user may have navigated away from.
          this.uiService.announceError({
            message: 'Template preparation failed',
            detailedMessage:
              'We could not prepare your CloudFormation templates. Try again — if you have already deployed, the existing templates remain valid.',
            sticky: true,
          })
        } else if (outcome === 'timeout') {
          this.uiService.announceError({
            message: 'Template preparation is taking longer than expected',
            detailedMessage:
              'Templates may still be generating in the background. You can run the commands now, and refresh later if anything looks out of date.',
            sticky: true,
          })
        }
      }),
      // HTTP errors from getJob (401/403/5xx) go through the standard handler so auth flow
      // and toasts behave like any other API failure; completing with EMPTY ensures finalize
      // in the caller still unblocks the UI.
      catchError((error) => {
        handleArpioError(window.location.href, this.apiService, this.uiService)(error)
        return EMPTY
      })
    )
  }
}
